import React, { useState, useEffect } from 'react';
import { getHubSpotDealsDetailed } from '../services/api';
import { useLayout } from '../context/LayoutContext';

// Calculate Thu-Wed week boundaries (last completed cycle)
function getWeekBoundaries(referenceDate = new Date()) {
  const d = new Date(referenceDate);

  // Find the most recent Wednesday that has already passed (yesterday or earlier)
  const yesterday = new Date(d);
  yesterday.setDate(d.getDate() - 1);
  const dayOfYesterday = yesterday.getDay(); // 0=Sun..6=Sat
  const diffToWed = (dayOfYesterday - 3 + 7) % 7; // 3 = Wednesday
  const weekEnd = new Date(yesterday);
  weekEnd.setDate(yesterday.getDate() - diffToWed);
  weekEnd.setHours(23, 59, 59, 999);

  // Week start = Thursday = 6 days before Wednesday
  const weekStart = new Date(weekEnd);
  weekStart.setDate(weekEnd.getDate() - 6);
  weekStart.setHours(0, 0, 0, 0);

  return { start: weekStart, end: weekEnd };
}

function getQuarterFromDate(date) {
  const month = date.getMonth(); // 0-11
  return Math.floor(month / 3) + 1;
}

function formatDate(date) {
  const d = new Date(date);
  const day = d.getDate();
  const month = d.getMonth() + 1;
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

const QUARTER_DATE_RANGES = {
  1: { start: '1/1', end: '31/3' },
  2: { start: '1/4', end: '30/6' },
  3: { start: '1/7', end: '30/9' },
  4: { start: '1/10', end: '31/12' }
};

function WeeklyDealsProgression() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dealsData, setDealsData] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, -1 = last week, etc.
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOwnerFilter, setSelectedOwnerFilter] = useState('');
  const { mobileLayout, toggleLayout } = useLayout();
  const recordsPerPage = 10;

  // Calculate week boundaries based on offset
  const today = new Date();
  const offsetDate = new Date(today);
  offsetDate.setDate(today.getDate() + weekOffset * 7);
  const { start: weekStart, end: weekEnd } = getWeekBoundaries(offsetDate);

  // Determine which quarter this week falls in (based on week start date)
  const selectedYear = weekStart.getFullYear();
  const selectedQuarter = getQuarterFromDate(weekStart);
  const quarterRange = QUARTER_DATE_RANGES[selectedQuarter];
  const quarterLabel = `Q${selectedQuarter} ${selectedYear}: FROM ${quarterRange.start}/${selectedYear} TO ${quarterRange.end}/${selectedYear}`;

  const weekDateLabel = `Week: ${formatDate(weekStart)} TO ${formatDate(weekEnd)}`;
  const weekLabel = weekOffset === 0 ? 'Current Week'
    : weekOffset === -1 ? 'Last Week'
    : weekOffset < 0 ? `${Math.abs(weekOffset)} Weeks Ago`
    : weekOffset === 1 ? 'Next Week'
    : `${weekOffset} Weeks Ahead`;

  useEffect(() => {
    fetchData();
    setCurrentPage(1);
    setSelectedOwnerFilter('');
  }, [weekOffset]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getHubSpotDealsDetailed(selectedYear, selectedQuarter);
      setDealsData(data);
    } catch (err) {
      setError('Failed to fetch deals data. Make sure the backend is running.');
      console.error('Error fetching deals:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  // All quarter deals
  const allQuarterDeals = dealsData?.deals || [];

  // Parse close_date (DD/MM/YYYY) and filter to current week
  const weekStartTime = weekStart.getTime();
  const weekEndTime = weekEnd.getTime();

  const deals = allQuarterDeals.filter(d => {
    if (!d.close_date) return false;
    const parts = d.close_date.split('/');
    if (parts.length !== 3) return false;
    const closeDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    return closeDate.getTime() >= weekStartTime && closeDate.getTime() <= weekEndTime;
  });

  // Get unique deal owners for filter
  const uniqueOwners = [...new Set(deals.map(d => d.deal_owner).filter(Boolean))].sort();

  // Filter deals by selected owner
  const filteredDeals = selectedOwnerFilter
    ? deals.filter(d => d.deal_owner === selectedOwnerFilter)
    : deals;

  const totalPages = Math.ceil(filteredDeals.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedDeals = filteredDeals.slice(startIndex, endIndex);

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header" style={{
        display: 'flex',
        flexDirection: mobileLayout ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: mobileLayout ? 'flex-start' : 'center',
        marginBottom: '20px',
        gap: mobileLayout ? '12px' : '0'
      }}>
        <div>
          <h1 style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: mobileLayout ? '16px' : undefined
          }}>
            <span style={{ color: '#f97316' }}>&#9734;</span>
            Deals Weekly Progression
          </h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
            Week cycle: Thursday to Wednesday
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Layout Toggle */}
          <button
            onClick={toggleLayout}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: mobileLayout ? '#3b82f6' : '#fff',
              color: mobileLayout ? '#fff' : '#1e293b',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {mobileLayout ? 'Mobile' : 'Desktop'}
          </button>
          {/* Week Navigation */}
          <button
            onClick={() => setWeekOffset(w => w - 1)}
            style={{
              padding: '8px 14px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: '#1e40af'
            }}
          >
            &larr; Prev Week
          </button>
          <button
            onClick={() => setWeekOffset(0)}
            disabled={weekOffset === 0}
            style={{
              padding: '8px 14px',
              borderRadius: '6px',
              border: '2px solid #f97316',
              background: weekOffset === 0 ? '#fff7ed' : '#fff',
              cursor: weekOffset === 0 ? 'default' : 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              color: '#c2410c'
            }}
          >
            This Week
          </button>
          <button
            onClick={() => setWeekOffset(w => w + 1)}
            style={{
              padding: '8px 14px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: '#1e40af'
            }}
          >
            Next Week &rarr;
          </button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Summary Stats */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <div style={{
          background: '#eff6ff',
          padding: '12px 20px',
          borderRadius: '8px',
          border: '1px solid #93c5fd'
        }}>
          <div style={{ fontSize: '12px', color: '#1e40af' }}>Week Period</div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#1d4ed8' }}>
            {formatDate(weekStart)} TO {formatDate(weekEnd)}
          </div>
        </div>
        <div style={{
          background: '#fff7ed',
          padding: '12px 20px',
          borderRadius: '8px',
          border: '2px solid #fb923c'
        }}>
          <div style={{ fontSize: '12px', color: '#c2410c' }}>{weekLabel}</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#ea580c' }}>
            Q{selectedQuarter}
          </div>
        </div>
        <div style={{
          background: '#faf5ff',
          padding: '12px 20px',
          borderRadius: '8px',
          border: '1px solid #d8b4fe'
        }}>
          <div style={{ fontSize: '12px', color: '#7c3aed' }}>Q{selectedQuarter} Total Deals</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#7c3aed' }}>
            {allQuarterDeals.length}
          </div>
        </div>
        <div style={{
          background: '#f0fdf4',
          padding: '12px 20px',
          borderRadius: '8px',
          border: '1px solid #86efac'
        }}>
          <div style={{ fontSize: '12px', color: '#166534' }}>This Week's Deals</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#15803d' }}>
            {deals.length}
          </div>
        </div>
        <div style={{
          background: '#ecfeff',
          padding: '12px 20px',
          borderRadius: '8px',
          border: '1px solid #67e8f9'
        }}>
          <div style={{ fontSize: '12px', color: '#0e7490' }}>Week Amount</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#0891b2' }}>
            {deals.reduce((sum, d) => sum + (d.amount || 0), 0).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Deals Table */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        padding: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <h3 style={{ margin: '0', fontSize: '16px', color: '#2563eb' }}>
            Sales Revenue Forecast & Commit_Q{selectedQuarter}_{selectedYear}
          </h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{
              background: '#dbeafe',
              color: '#1e40af',
              padding: '4px 10px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '500'
            }}>
              {quarterLabel}
            </span>
            <span style={{
              background: '#fef3c7',
              color: '#92400e',
              padding: '4px 10px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '500'
            }}>
              {weekDateLabel}
            </span>
            <select
              value={selectedOwnerFilter}
              onChange={(e) => {
                setSelectedOwnerFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: '4px 10px',
                borderRadius: '4px',
                border: '1px solid #e2e8f0',
                fontSize: '11px',
                cursor: 'pointer',
                background: selectedOwnerFilter ? '#fef3c7' : '#fff',
                color: '#1e293b'
              }}
            >
              <option value="">All Deal Owners</option>
              {uniqueOwners.map(owner => (
                <option key={owner} value={owner}>{owner}</option>
              ))}
            </select>
            <span style={{
              background: '#f0fdf4',
              color: '#166534',
              padding: '4px 10px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '500'
            }}>
              {filteredDeals.length} deals {selectedOwnerFilter && '(filtered)'}
            </span>
          </div>
        </div>

        {filteredDeals.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#64748b',
            fontSize: '15px'
          }}>
            No deals found for Q{selectedQuarter} {selectedYear}. Try navigating to a different week.
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1600px', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
                    <th style={{ textAlign: 'left', padding: '12px 10px', color: '#1e40af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Company</th>
                    <th style={{ textAlign: 'left', padding: '12px 10px', color: '#1e40af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Sales Region</th>
                    <th style={{ textAlign: 'left', padding: '12px 10px', color: '#1e40af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Deal Owner</th>
                    <th style={{ textAlign: 'left', padding: '12px 10px', color: '#1e40af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Deal Name</th>
                    <th style={{ textAlign: 'left', padding: '12px 10px', color: '#1e40af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Deal Source</th>
                    <th style={{ textAlign: 'left', padding: '12px 10px', color: '#1e40af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Currency</th>
                    <th style={{ textAlign: 'right', padding: '12px 10px', color: '#1e40af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Amount</th>
                    <th style={{ textAlign: 'right', padding: '12px 10px', color: '#1e40af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Actual ACV</th>
                    <th style={{ textAlign: 'right', padding: '12px 10px', color: '#1e40af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Expected ACV</th>
                    <th style={{ textAlign: 'left', padding: '12px 10px', color: '#1e40af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Actual Duration</th>
                    <th style={{ textAlign: 'left', padding: '12px 10px', color: '#1e40af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Deal Stage</th>
                    <th style={{ textAlign: 'left', padding: '12px 10px', color: '#1e40af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Close Plan %</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDeals.map((deal, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '10px', fontSize: '12px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={deal.company}>{deal.company || '-'}</td>
                      <td style={{ padding: '10px', fontSize: '12px', whiteSpace: 'nowrap' }}>{deal.sales_region || '-'}</td>
                      <td style={{ padding: '10px', fontSize: '12px', whiteSpace: 'nowrap' }}>{deal.deal_owner || '-'}</td>
                      <td style={{ padding: '10px', fontSize: '12px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={deal.deal_name}>{deal.deal_name || '-'}</td>
                      <td style={{ padding: '10px', fontSize: '12px', whiteSpace: 'nowrap' }}>{deal.deal_source || '-'}</td>
                      <td style={{ padding: '10px', fontSize: '12px', whiteSpace: 'nowrap' }}>{deal.currency || 'USD'}</td>
                      <td style={{ padding: '10px', fontSize: '12px', textAlign: 'right', fontWeight: '500', whiteSpace: 'nowrap' }}>
                        {deal.amount > 0 ? deal.amount.toLocaleString() : '-'}
                      </td>
                      <td style={{ padding: '10px', fontSize: '12px', textAlign: 'right', fontWeight: '500', whiteSpace: 'nowrap' }}>
                        {deal.actual_acv > 0 ? deal.actual_acv.toLocaleString() : '-'}
                      </td>
                      <td style={{ padding: '10px', fontSize: '12px', textAlign: 'right', fontWeight: '500', whiteSpace: 'nowrap' }}>
                        {deal.expected_acv > 0 ? deal.expected_acv.toLocaleString() : '-'}
                      </td>
                      <td style={{ padding: '10px', fontSize: '12px', whiteSpace: 'nowrap' }}>{deal.actual_duration || '-'}</td>
                      <td style={{ padding: '10px', fontSize: '12px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={deal.deal_stage}>{deal.deal_stage || '-'}</td>
                      <td style={{ padding: '10px', fontSize: '12px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={deal.close_plan}>{deal.close_plan || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{
              display: 'flex',
              flexDirection: mobileLayout ? 'column' : 'row',
              justifyContent: mobileLayout ? 'center' : 'space-between',
              alignItems: 'center',
              marginTop: '16px',
              padding: '12px 0',
              gap: '10px'
            }}>
              <div style={{ fontSize: mobileLayout ? '11px' : '13px', color: '#64748b', textAlign: 'center' }}>
                Showing {filteredDeals.length > 0 ? startIndex + 1 : 0} to {Math.min(endIndex, filteredDeals.length)} of {filteredDeals.length} deals
                {selectedOwnerFilter && ` (filtered by ${selectedOwnerFilter})`}
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center' }}>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    background: currentPage === 1 ? '#f1f5f9' : '#fff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    color: currentPage === 1 ? '#94a3b8' : '#1e40af'
                  }}
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    background: currentPage === 1 ? '#f1f5f9' : '#fff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    color: currentPage === 1 ? '#94a3b8' : '#1e40af'
                  }}
                >
                  Previous
                </button>
                <span style={{ padding: '4px 8px', fontSize: '13px', color: '#1e293b' }}>
                  {currentPage}/{totalPages || 1}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    background: currentPage === totalPages ? '#f1f5f9' : '#fff',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    color: currentPage === totalPages ? '#94a3b8' : '#1e40af'
                  }}
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages || totalPages === 0}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    background: currentPage === totalPages ? '#f1f5f9' : '#fff',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    color: currentPage === totalPages ? '#94a3b8' : '#1e40af'
                  }}
                >
                  Last
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default WeeklyDealsProgression;
